import React, { useState, useEffect, useRef } from "react";
import Map, { Marker, ViewState } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Trip, Location } from "@/types";
import { Minimize2, Calendar } from "lucide-react";
import { useSelectedDayItems } from "@/hooks/useSelectedDayItems";
import { getLocationTypeIcon, toTitleCase } from "@/lib/general";
import { Tooltip } from "@heroui/tooltip";
import { HomeIcon, CalendarIcon } from "@heroicons/react/24/solid";
import mapboxgl from "mapbox-gl";

interface DashboardMapProps {
  trip: Trip;
  selectedLocation: Location | null;
}

// Custom marker component for itinerary items
const CustomMarker = ({ locationDetails }: { locationDetails: any }) => {
  const getMarkerContent = () => {
    switch (locationDetails.activityType) {
      case "accommodation":
        return (
          <div className="bg-white hover:bg-neutral-50 rounded-full ring-1 ring-neutral-700 hover:ring-neutral-800 w-7 h-7 flex items-center justify-center transition-all duration-200 shadow-md hover:shadow-lg">
            <HomeIcon className="w-3.5 h-3.5 text-neutral-700 group-hover:text-neutral-800 transition-colors" />
          </div>
        );
      case "flight":
        return (
          <div className="bg-white hover:bg-neutral-50 rounded-full ring-1 ring-neutral-600 hover:ring-neutral-700 w-7 h-7 flex items-center justify-center transition-all duration-200 shadow-md hover:shadow-lg">
            <i className="fa-solid fa-plane text-neutral-600 text-[12px]"></i>
          </div>
        );
      default:
        return (
          <div className="bg-white hover:bg-indigo-50 rounded-full ring-1 ring-indigo-600 hover:ring-indigo-700 w-7 h-7 flex items-center justify-center transition-all duration-200 shadow-md hover:shadow-lg">
            <i
              className={`fa-solid ${
                locationDetails.categories &&
                locationDetails.categories.length > 0
                  ? getLocationTypeIcon(
                      toTitleCase(locationDetails.categories[0])
                    )
                  : "fa-map-pin"
              } text-indigo-600 text-[12px]`}
            ></i>
          </div>
        );
    }
  };

  return (
    <Tooltip
      content={
        <div className="flex flex-row items-center justify-center gap-2 p-1">
          {locationDetails.activityType === "accommodation" ? (
            <div className="flex items-center gap-2">
              <HomeIcon className="w-3.5 h-3.5 text-neutral-700 group-hover:text-neutral-800 transition-colors" />
              <p className="text-[13px] font-normal text-neutral-800 truncate max-w-[200px]">
                {locationDetails?.name || ""}
              </p>
            </div>
          ) : locationDetails.activityType === "flight" ? (
            <div className="flex items-center gap-2">
              <i className="fa-solid fa-plane text-blue-600 text-[12px]"></i>
              <p className="text-[13px] font-normal text-neutral-800 truncate max-w-[200px]">
                {locationDetails?.name || ""}
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <i
                className={`fa-solid ${
                  locationDetails.categories &&
                  locationDetails.categories.length > 0
                    ? getLocationTypeIcon(locationDetails.categories[0])
                    : "fa-map-pin"
                } text-indigo-600 text-[12px]`}
              ></i>
              <p className="text-[13px] font-normal text-neutral-800 truncate max-w-[200px]">
                {locationDetails?.name || ""}
              </p>
            </div>
          )}
        </div>
      }
      onClick={() => {}}
      closeDelay={0}
      radius="md"
      showArrow={true}
      classNames={{
        content: "shadow-lg border border-neutral-100",
      }}
    >
      {getMarkerContent()}
    </Tooltip>
  );
};

// Add this new component for location markers
const LocationMarker = ({
  location,
  isSelected,
  onSelect,
}: {
  location: Location;
  isSelected: boolean;
  onSelect: (location: Location) => void;
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(location);
  };

  return (
    <Tooltip
      content={
        <div className="flex flex-col p-1">
          <p className="text-[13px] font-medium text-neutral-800">
            {location.name}
          </p>
          <p className="text-[11px] text-neutral-600">
            {location.address_components.city},{" "}
            {location.address_components.country}
          </p>
        </div>
      }
      onClick={handleClick}
      closeDelay={0}
      radius="md"
      showArrow={true}
      classNames={{
        content: "shadow-lg border border-neutral-100",
      }}
    >
      <div
        className={`cursor-pointer transition-all duration-300 ${
          isSelected
            ? "bg-indigo-600 ring-4 ring-indigo-200 scale-110"
            : "bg-white hover:bg-indigo-50 ring-1 ring-indigo-600 hover:ring-indigo-700"
        } rounded-full w-8 h-8 flex items-center justify-center shadow-md hover:shadow-lg`}
        onClick={handleClick}
      >
        {location.origin ? (
          <HomeIcon
            className={`w-4 h-4 ${
              isSelected ? "text-white" : "text-indigo-600"
            }`}
          />
        ) : (
          <i
            className={`fa-solid fa-location-dot text-[14px] ${
              isSelected ? "text-white" : "text-indigo-600"
            }`}
          ></i>
        )}
      </div>
    </Tooltip>
  );
};

const DashboardMap: React.FC<DashboardMapProps> = ({
  trip,
  selectedLocation,
}) => {
  const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN as string;
  const [viewport, setViewport] = useState<ViewState>({
    longitude: trip.locations[0]!.coordinates.longitude,
    latitude: trip.locations[0]!.coordinates.latitude,
    zoom: 12,
    bearing: 0,
    pitch: 0,
    padding: { top: 0, bottom: 0, left: 0, right: 0 },
  });
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);

  // Get itinerary items for the selected location and day
  const dayItems = useSelectedDayItems(
    selectedLocation?.itinerary_items?.[selectedDayIndex]?.date || "",
    selectedLocation?.id
  );

  // Calculate bounds to fit all dayItems
  const fitBoundsToItems = () => {
    if (!mapRef.current || !dayItems.length || !selectedLocation) return;

    // Create a bounds object
    const bounds = new mapboxgl.LngLatBounds();

    // Add the selected location to the bounds
    bounds.extend([
      selectedLocation.coordinates.longitude,
      selectedLocation.coordinates.latitude,
    ]);

    // Extend the bounds to include all dayItems
    dayItems.forEach((item) => {
      if (item.coordinates) {
        bounds.extend([item.coordinates.longitude, item.coordinates.latitude]);
      }
    });

    // Add padding to the bounds (in pixels)
    const padding = { top: 100, bottom: 100, left: 100, right: 100 };

    // Fit the map to the bounds with padding and smooth animation
    mapRef.current.fitBounds(bounds, {
      padding,
      duration: 1000,
      maxZoom: 15, // Prevent zooming in too much if items are close together
    });
  };

  // Update map bounds when dayItems change
  useEffect(() => {
    if (dayItems.length > 0 && mapRef.current && isMapLoaded) {
      fitBoundsToItems();
    } else if (
      !dayItems.length &&
      selectedLocation &&
      mapRef.current &&
      isMapLoaded
    ) {
      // If no dayItems, center on the selected location
      mapRef.current.flyTo({
        center: [
          selectedLocation.coordinates.longitude,
          selectedLocation.coordinates.latitude,
        ],
        zoom: 11,
        duration: 1000,
      });
    }
  }, [dayItems, selectedLocation, isMapLoaded]);

  // Skip flying to the origin location
  useEffect(() => {
    if (!selectedLocation || !mapRef.current || !isMapLoaded) return;

    // Skip flying to the origin location
    if (selectedLocation.origin) return;

    // If we have dayItems, fit bounds to include all items
    if (dayItems.length > 0) {
      fitBoundsToItems();
    } else {
      // Otherwise just center on the selected location
      mapRef.current.flyTo({
        center: [
          selectedLocation.coordinates.longitude,
          selectedLocation.coordinates.latitude,
        ],
        zoom: 11,
        duration: 2000,
        essential: true,
        curve: 1,
        easing: (t) => t,
        linear: true,
      });
    }
  }, [selectedLocation, dayItems, isMapLoaded]);

  const handleZoomOut = () => {
    if (trip.locations?.length > 0 && mapRef.current) {
      mapRef.current.flyTo({
        center: [
          trip.locations[0]!.coordinates.longitude,
          trip.locations[0]!.coordinates.latitude,
        ],
        zoom: 2.5,
        duration: 1500,
      });
    }
  };

  const handleDayChange = (day: any, index: number) => {
    setSelectedDayIndex(index);
    setIsOpen(false);

    // Allow time for the dayItems to update before fitting bounds
    if (mapRef.current && isMapLoaded) {
      fitBoundsToItems();
    }
  };

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="flex h-full">
      {/* Map Container */}
      <div className="w-full h-full">
        <div className="h-full overflow-hidden rounded-md">
          <div className="h-full relative">
            {/* Day Selector Dropdown */}
            {selectedLocation && !selectedLocation.origin && (
              <div className="absolute top-2 right-2 z-10">
                <div className="relative">
                  <button
                    onClick={handleToggleDropdown}
                    className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium bg-white/80 backdrop-blur-sm border border-neutral-400 hover:border-neutral-600 rounded-full transition-all duration-200 hover:bg-white group"
                  >
                    <CalendarIcon className="w-3.5 h-3.5 text-neutral-500 group-hover:text-neutral-700 transition-colors" />
                    <span className="text-neutral-500 group-hover:text-neutral-700 transition-colors">
                      Day {selectedDayIndex !== -1 ? selectedDayIndex + 1 : ""}
                    </span>
                    <i
                      className={`fa-solid fa-chevron-${
                        isOpen ? "up" : "down"
                      } text-[9px] text-neutral-400 group-hover:text-neutral-600 transition-transform duration-200`}
                    ></i>
                  </button>

                  {isOpen && (
                    <div className="absolute top-10 right-0 w-44 bg-white/95 backdrop-blur-sm border border-neutral-200 rounded-xl shadow-lg py-1 animate-in fade-in slide-in-from-top-2 duration-200">
                      <div className="max-h-[280px] overflow-y-auto no-scrollbar">
                        {selectedLocation?.itinerary_items?.map(
                          (day: any, index: number) => (
                            <button
                              key={index}
                              className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-xs transition-colors
                              ${
                                selectedDayIndex === index
                                  ? "bg-indigo-50/80 text-indigo-600"
                                  : "text-neutral-600 hover:bg-neutral-50"
                              }`}
                              onClick={() => handleDayChange(day, index)}
                            >
                              <div
                                className={`w-1 h-1 rounded-full transition-colors
                              ${
                                selectedDayIndex === index
                                  ? "bg-indigo-600"
                                  : "bg-neutral-300"
                              }`}
                              />
                              <span className="font-medium">
                                Day {index + 1}
                              </span>
                              {day.items?.length > 0 && (
                                <span className="ml-auto text-[10px] font-normal text-neutral-400">
                                  {day.items.length}{" "}
                                  {day.items.length === 1 ? "stop" : "stops"}
                                </span>
                              )}
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Zoom Out Button */}
            <button
              onClick={handleZoomOut}
              className="absolute bottom-2 right-2 z-10 bg-white backdrop-blur-sm w-8 h-8 rounded-full shadow-sm hover:bg-white transition-colors border border-neutral-100 flex items-center justify-center"
              title="Zoom out to see all locations"
            >
              <Minimize2 className="w-4 h-4 text-neutral-500" />
            </button>

            <Map
              {...viewport}
              mapboxAccessToken={mapboxToken}
              style={{ width: "100%", height: "100%" }}
              mapStyle="mapbox://styles/jamiedennis8/clxmyy67600fs01pu0d6q2ha7/draft"
              onMove={(evt) => setViewport(evt.viewState)}
              onLoad={(evt) => {
                setIsMapLoaded(true);
                mapRef.current = evt.target;
              }}
              attributionControl={false}
            >
              {/* Activity Markers */}
              {dayItems.map((item, index) => {
                if (!item.coordinates) return null;
                return (
                  <Marker
                    key={`${item.id}-${index}`}
                    longitude={item.coordinates.longitude}
                    latitude={item.coordinates.latitude}
                    anchor="center"
                  >
                    <CustomMarker locationDetails={item} />
                  </Marker>
                );
              })}
            </Map>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardMap;
